import { faCheckCircle, faClock, faHourglassHalf, faTimesCircle, faBan, faPlus, faInfoCircle, faCircleXmark, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getSaiTerminology } from '../../../config/terminology'
import { NavLink, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react';

import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import SideBar from '../../SideBar'
import moment from 'moment';
import useAuth from '../../../hooks/useAuth'
import ViewContacts from './Components/ViewContacts'
import MarketingModal from './Components/MarketingModal';
import InviteMeetingModal from './Components/InviteMeetingModal'
import ReferralModal from './Components/ReferralModal'
import Slideout from '../../../components/SlideOut'

const Dashboard = ({ onNavigate }) => {
  const axios = useAxiosPrivate();
  const navigate = useNavigate()
  const { auth } = useAuth()
  const [policyData, setPolicyData] = useState(null)
  const terms = getSaiTerminology(policyData)
  const [claims, setClaims] = useState([])
  const [outstandingTotal, setOutstandingTotal] = useState(null)
  const expiryDate = moment(policyData?.policy_expire).endOf('day')
  const policyStart = moment(policyData?.policy_inception).startOf('day')
  const policyEnd = moment(policyData?.policy_expire).endOf('day')
  const today = moment().startOf('day')
  const totalDays = policyEnd.diff(policyStart, 'days')
  const daysElapsed = today.diff(policyStart, 'days')
  const daysRemaining = policyEnd.diff(today, 'days')
  const progressPercent = Math.min((daysElapsed / totalDays) * 100, 100)
  const totalClaims = claims.length
  const [open, setOpen] = useState(false)
  const [openMarketing, setOpenMarketing] = useState(false)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const openUpgrade = () => setIsUpgradeModalOpen(true)
  const closeUpgrade = () => setIsUpgradeModalOpen(false)
  const [showBenefitsModal, setShowBenefitsModal] = useState(false)
  const [openReferral, setOpenReferral] = useState(false)
  const [openInvite, setOpenInvite] = useState(false)
  const [quotes, setQuotes] = useState([])
  const toggleModal = () => {
    setShowBenefitsModal(!showBenefitsModal)
  }

  const groupedStatusIds = {
    pending: [1, 37],
    in_progress: [2, 3, 4, 12, 13, 18, 19, 22, 23, 27, 28, 32, 33, 34],
    accepted: [5, 6, 14, 15, 16, 21, 24, 26, 35, 36],
    rejected: [8, 9, 10, 11, 17, 29, 30],
    closed: [7, 20, 25, 31],
    awaiting_info: [],
  }

  const getClaimCount = (groupKey, claimsList = []) => {
    const statusIds = groupedStatusIds[groupKey] || []
    if (groupKey === 'awaiting_info') {
      return claimsList.filter(claim => claim.claim_documents_requested === 1).length
    }
    return claimsList.filter(claim =>
      statusIds.includes(claim.claim_status_id)
    ).length
  }

  useEffect(() => {
    setPolicyData(JSON.parse(localStorage.getItem('policyData')))
    const org = JSON.parse(localStorage.getItem('orgType'))

    if (!policyData?.policy_id) return

    const fetchDashboardData = async () => {
      await Promise.all([
        axios.get(`/v1/staff-hub/policy/invoices/${policyData.policy_id}`)
          .then(invoiceRes => {
            const sentInvoices = (invoiceRes.data?.invoices || []).filter(invoice => {
              return invoice.invoice_status === 'sent' && moment().diff(moment(invoice.invoice_action_date), 'days') > 30
            })

            const totalCost = sentInvoices.reduce((total, invoice) => {
              return total + parseFloat(invoice.invoice_cost || 0)
            }, 0).toFixed(2)

            setOutstandingTotal(totalCost || 0)
          })
          .catch(err => {
            console.error('Dashboard invoices fetch error:', err)
            setOutstandingTotal(0)
          }),
        axios.get(`/v1/staff-hub/policy/claims/${policyData.policy_id}`)
          .then(claimsRes => {
            console.log(claimsRes)
            setClaims(claimsRes.data?.claims || [])
          })
          .catch(err => {
            console.error('Dashboard claims fetch error:', err)
            setClaims([])
          }),
        axios.get(`/v1/staff-hub/renewal/quotes/${policyData.policyable_id}`)
          .then(quotesRes => {
            setQuotes(quotesRes || [])
          })
          .catch(err => {
            console.error('Dashboard quotes fetch error:', err)
            setQuotes([])
          }),
      ])
    };

    fetchDashboardData()
  }, [policyData?.policy_id])

  const openContacts = () => {
    setOpen(true)
  }

  const openRefer = () => {
    setOpenReferral(true)
    setOpenMarketing(false)
  }

  const openInviteModal = () => {
    setOpenInvite(true)
    setOpenMarketing(false)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      const storedValue = localStorage.getItem('marketing')

      if (storedValue) {
        const storedDate = moment(storedValue)
        const currentDate = moment()
        const diffInDays = currentDate.diff(storedDate, 'days')

        if (storedDate.isValid() && diffInDays >= 90) {
          localStorage.removeItem('marketing')
          setOpenMarketing(true)
        }
      } else {
        setOpenMarketing(true)
      }
    }, 30000)

    return () => clearTimeout(timer)
  }, [])

  const handleCloseMarketingModal = () => {
    localStorage.setItem('marketing', moment().toISOString())
    setOpenMarketing(false)
  }

  const handleGoToClaims = () => {
    if (onNavigate) {
      onNavigate('/sai/claims')
      return
    }

    navigate('/sai/claims')
  }

  const handleSetContactsOpen = (nextOpen) => {
    setOpen(nextOpen)
  }

  return (
    <div className="w-full">
      {isUpgradeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg relative">
            <button
              onClick={closeUpgrade}
              className="absolute top-2 right-2 text-gray-500 hover:text-red-600 cursor-pointer"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </button>
            <h2 className="text-xl font-bold mb-4 text-sas">Contact Us For More Details</h2>
            {policyData?.department === 'Mutual' || policyData?.department === "GBS" ? (
              <a href='tel:01773814400' className="mb-2 cursor-pointer"><strong>Phone:</strong> 01773 814 400</a>
            ) : (
              <a href='tel:01773851079' className="mb-2 cursor-pointer"><strong>Phone:</strong> 01773 851 079</a>
            )}
            {policyData?.department === 'Mutual' || policyData?.department === "GBS" ? (
              <p className="mb-2"><strong>Email:</strong> <a href="mailto:enquiries@schoolsmutual.co.uk" className="text-sas underline cursor-pointer">enquiries@schoolsmutual.co.uk</a></p>
            ) : (
              <p className="mb-2"><strong>Email:</strong> <a href="mailto:support@schoolsadvisoryservice.co.uk" className="text-sas underline cursor-pointer">support@schoolsadvisoryservice.co.uk</a></p>
            )}
            <p className="text-sm text-gray-600 mt-4">Our team will be happy to assist with upgrades and optional extras.</p>
          </div>
        </div>
      )}
      {openMarketing && <MarketingModal openMarketing={openMarketing} setOpenMarketing={setOpenMarketing} onRefer={openRefer} onInvite={openInviteModal} handleClose={handleCloseMarketingModal} />}
      {showBenefitsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-red-600 cursor-pointer"
              onClick={toggleModal}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </button>
            <h3 className="text-lg font-bold text-[#009CDE] mb-4">Additional Policy Benefits</h3>
            <div className='grid grid-cols-2 gap-2'>
              <ul>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Bereavement</li>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Compassionate leave</li>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Jury Service</li>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Dedicated account manager</li>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Suspension</li>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Stranded staff abroad</li>
              </ul>
              <ul className="space-y-2">
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Ofsted Inspection engagement duties</li>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Trade union/association duties</li>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Online claims portal</li>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Stress related absences <span className="text-xs text-gray-500">(where purchased)</span></li>
                <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Maternity/Parental leave <span className="text-xs text-gray-500">(where purchased)</span></li>
              </ul>
            </div>
            <div className='mt-8'>
              <p className="font-semibold mb-2">Upgrade to SAS Policy Plus for:</p>
              <ul className="list-none space-y-1">
                <li> <FontAwesomeIcon icon={faPlus} className="text-sas mr-2" />Pre-existing conditions</li>
                <li> <FontAwesomeIcon icon={faPlus} className="text-sas mr-2" />Pre-planned surgery</li>
              </ul>
              <div onClick={() => { openUpgrade(); setShowBenefitsModal(false); }} className="text-sas cursor-pointer underline text-xs mt-2 inline-block">Contact us for more details</div>
            </div>

          </div>
        </div>
      )}
      {/* Main Content Layout */}
      <div>
        {/* Main Card Section */}
        <main className="w-full space-y-4 p-6">
          <div className="rounded-md bg-[#FFECD1] p-4 border border-[#F8CF94]">
            <div className="flex">
              <div className="flex-shrink-0">
                {/* <CheckCircleIcon aria-hidden="true" className="h-5 w-5 text-green-400" /> */}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-[#B57B29]">
                  <strong>Please Note:</strong> You must complete your staff list, holidays and account information in order to log/view your {terms.claim.pluralLower}.
                </p>
              </div>
            </div>
          </div>

          <div className='flex items-center justify-between'>
            <h2 className="text-xl font-semibold text-sas-dark">
              {terms.policy.singularTitle} Summary for {auth.schools[0]?.name || 'your school'}
            </h2>

            <button
              type="button"
              onClick={() => openContacts()}
              className="inline-flex items-center border bg-white border-sas-dark text-sas-dark rounded-md overflow-hidden text-sm font-medium cursor-pointer">
              <span className="px-4 py-2">View {terms.policy.singularTitle} Contacts</span>
              <span className="bg-sas-dark text-white px-3 py-2">
                <FontAwesomeIcon icon={faPlus} />
              </span>
            </button>
          </div>
          {/* Policy Card */}
          <div className='text-sas-dark'>
            <div className='border-b pb-4 mb-4'>
              <p className="mb-2">{terms.policy.singularTitle} Reference: <strong>{policyData?.policy_reference}</strong></p>
              <p className="mb-2">Inception Date: <strong>{moment(policyData?.policy_inception).format('DD/MM/YYYY') || 'N/A'}</strong></p>
              <p className="mb-2">Renewal Date: <strong>{moment(policyData?.policy_expire).format('DD/MM/YYYY') || 'N/A'}</strong></p>
              <p className="mb-2">Account Manager: <strong className="text-sas hover:underline cursor-pointer">{policyData?.policy_owner_name || ''}</strong></p>
            </div>
            {policyData?.department === "Mutual" || policyData?.department === "GBS" ? (
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div>
                  <p className="font-semibold mb-2">Included in your {policyData?.product} {terms.policy.singularTitle}:</p>
                  <ul className="space-y-1 text-gray-800">
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Jury service</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Sickness</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Suspension</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Bereavement leave</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Stress-related absences</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Compassionate leave</li>
                  </ul>
                </div>

                <div>
                  <ul className="mt-[32px] space-y-1 text-gray-800">
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Stranded staff</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Accidents in or out of work</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Phased return to work</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Adoption leave</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Maternity leave</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Paternity leave</li>
                  </ul>
                </div>

                {/* Upgrade Info */}
                {policyData?.product?.toLowerCase().includes("enhanced") ? (
                  <div>
                    <ul className="mt-[32px] space-y-1 text-gray-800">
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Pre-existing conditions</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Pre-planned operations</li>
                    </ul>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold mb-2">Upgrade to Enhanced Protection Option for:</p>
                    <p className="text-sm"> <FontAwesomeIcon icon={faPlus} className="text-[#8acb94] mr-2" />Pre-existing conditions  </p>
                    <p className="text-sm"> <FontAwesomeIcon icon={faPlus} className="text-[#8acb94] mr-2" />Pre-planned surgery </p>
                    <p onClick={() => openUpgrade()} className="text-[#8acb94] underline cursor-pointer mt-4">Contact us for more details</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div>
                  <p className="font-semibold mb-2">Included in your {policyData?.product || terms.policy.singularTitle}:</p>
                  <ul className="list-none space-y-2">
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Sickness or injury absences</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Accidents in or out of work</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Phased returns to work</li>
                    <li onClick={toggleModal} className="cursor-pointer"><span className="text-sas">
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </span> <span className="text-sas underline">View all benefits and more information</span></li>
                  </ul>
                </div>

                {/* Upgrade Info */}
                <div>
                  <p className="font-semibold mb-2">Upgrade to SAS Policy Plus for:</p>
                  <ul className="list-none space-y-1">
                    <li> <FontAwesomeIcon icon={faPlus} className="text-sas mr-2" />Pre-existing conditions</li>
                    <li> <FontAwesomeIcon icon={faPlus} className="text-sas mr-2" />Pre-planned surgery</li>
                  </ul>
                  <div onClick={() => openUpgrade()} className="text-sas cursor-pointer underline text-xs mt-2 inline-block">Contact us for more details</div>
                </div>

                {/* Optional Extras */}
                <div>
                  <p className="font-semibold mb-2">Optional Extras</p>
                  <ul className="list-none space-y-1 text-red-600">
                    <li><FontAwesomeIcon icon={faPlus} className=" mr-2" /> Travel Insurance</li>
                    <li><FontAwesomeIcon icon={faPlus} className=" mr-2" /> Cyber Insurance</li>
                  </ul>
                  <div onClick={() => openUpgrade()} className="text-sas cursor-pointer underline text-xs mt-2 inline-block">Contact us for more details</div>
                </div>
              </div>
            )}
          </div>

          {/* Renewal + Invoice Section */}
          <div className="flex flex-col md:flex-row md:space-x-6">
            {/* Renewal */}
            <div className="p-4 w-full md:w-1/2 mb-4 md:mb-0">
              <p className="text-sm font-semibold text-gray-700">Your Renewal</p>
              <p className="text-xl font-bold text-sas-dark mb-2">
                {expiryDate.format('Do MMMM YYYY')}
              </p>

              <div className="w-full bg-gray-200 h-2 rounded-full mb-1">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${daysRemaining <= 0
                    ? 'bg-red-500'
                    : daysRemaining < 45
                      ? 'bg-orange-400'
                      : 'bg-sas'
                    }`}
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>

              <div className='flex items-center justify-between'>
                <p className={`text-xs ${daysRemaining <= 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  {daysRemaining > 0
                    ? `Your ${terms.policy.singularLower} expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`
                    : `Your ${terms.policy.singularLower} has expired`}
                </p>
                {/* {quotes.length > 0 && ( */}
                {/* <NavLink to={'/sai/quotes'} className='text-xs font-medium text-sas cursor-pointer hover:underline'>
                  View your new quotes
                </NavLink> */}
                {/* )} */}
              </div>
            </div>

            {/* Invoice */}
            <div className="bg-white rounded-xl border border-red-500 shadow-md p-6 w-full md:w-1/2">
              <p className="text-sm font-semibold text-red-600">Outstanding Invoices</p>
              <p className="text-2xl font-bold text-red-700 mb-1">
                {outstandingTotal !== null ? `£${outstandingTotal}` : 'Loading...'}
              </p>
              <p className="text-xs text-red-600">
                {outstandingTotal > 0 ? 'You have unpaid invoices' : 'No outstanding invoices'}
              </p>
            </div>
          </div>

          {/* Current Claims */}
          <div className="text-sas-dark p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Current {terms.claim.pluralTitle}</h3>
              <span className="text-sm font-semibold text-sas-dark">
                Total <span className="ml-1">{totalClaims}</span>
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm font-medium">
              <div className="flex items-center space-x-2 border rounded-lg p-4">
                <FontAwesomeIcon icon={faHourglassHalf} className="text-orange-500 text-lg" />
                <span>Pending <strong>{getClaimCount('pending', claims)}</strong></span>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4">
                <FontAwesomeIcon icon={faClock} className="text-yellow-400 text-lg" />
                <span>In Progress <strong>{getClaimCount('in_progress', claims)}</strong></span>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4">
                <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 text-lg" />
                <span>Closed <strong>{getClaimCount('closed', claims)}</strong></span>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-lg" />
                <span>Accepted <strong>{getClaimCount('accepted', claims)}</strong></span>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4">
                <FontAwesomeIcon icon={faBan} className="text-red-600 text-lg" />
                <span>Rejected <strong>{getClaimCount('rejected', claims)}</strong></span>
              </div>
            </div>

            <p className="text-sm text-sas-dark mt-4">
              <strong>{getClaimCount('awaiting_info', claims)}</strong> {terms.claim.pluralLower} are awaiting more information
            </p>

            <div className="text-right mt-2">
              <button type="button" onClick={handleGoToClaims} className="text-sm text-sas hover:underline cursor-pointer">
                Go to {terms.claim.pluralTitle}
              </button>
            </div>
          </div>
        </main>
      </div>
      <Slideout
        open={open}
        setOpen={handleSetContactsOpen}
        title={`View your ${terms.policy.singularTitle} Contacts`}
        width="md"
      >
        <ViewContacts
          open={open}
          policyData={policyData}
        />
      </Slideout>
      <ReferralModal openReferral={openReferral} setOpenReferral={setOpenReferral} />
      <InviteMeetingModal openInvite={openInvite} setOpenInvite={setOpenInvite} />
    </div>
  )
}

export default Dashboard
